import { useCallback } from 'react';

import { useLazyQuery } from '@apollo/client';

import GetCourseItemInfoQuery from 'bundles/enterprise-learning-path-description-page/queries/GetCourseItemInfo.graphql';
import GetItemsInCourseQuery from 'bundles/enterprise-learning-path-description-page/queries/GetItemsInCourse.graphql';
import type {
  GetCourseItemInfoQuery as GetCourseItemInfoData,
  GetCourseItemInfoQueryVariables,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/GetCourseItemInfo';
import type {
  GetItemsInCourseQuery as GetItemsInCourseData,
  GetItemsInCourseQueryVariables,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/GetItemsInCourse';

const useGetSkillPathwayCourseStartLink = (courseId: string, courseSlug: string) => {
  const [getItemsInCourse] = useLazyQuery<GetItemsInCourseData, GetItemsInCourseQueryVariables>(GetItemsInCourseQuery, {
    variables: {
      courseId,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  const [getCourseItemInfo] = useLazyQuery<GetCourseItemInfoData, GetCourseItemInfoQueryVariables>(
    GetCourseItemInfoQuery,
    {
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-first',
    }
  );

  // This returns the link to the first uncompleted item in the course or the course home link
  const fetchSkillPathwayCourseStartLink = useCallback<() => Promise<string>>(async () => {
    const courseHomeLink = `/learn/${courseSlug}/home/welcome`;

    try {
      const itemsInCourseResponse = await getItemsInCourse();
      const itemIdsInCourse =
        itemsInCourseResponse?.data?.NaptimeQueries?.OnDemandCoursesV1Resource?.get?.onDemandLearnerMaterials
          ?.itemIds || [];

      const itemsResponse = await getCourseItemInfo({
        variables: {
          courseId,
          itemIds: itemIdsInCourse,
        },
      });

      // Create a map of itemId to index to preserve the order from itemIdsInCourse
      const itemIdToIndex = new Map<string, number>();
      itemIdsInCourse.forEach((itemId, index) => {
        if (itemId) {
          itemIdToIndex.set(itemId, index);
        }
      });

      const orderedItems =
        itemsResponse?.data?.NaptimeQueries?.OnDemandLearnerMaterialItemsV1Resource?.items?.elements
          ?.slice()
          .sort((itemA, itemB) => {
            const indexA = itemIdToIndex.get(itemA?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
            const indexB = itemIdToIndex.get(itemB?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
          }) || [];

      const firstUncompletedItemInCourse = orderedItems.find((item) => !item?.isPassedOrCompleted);

      // Fallback to the first item if all items in the course are completed
      const firstItemInCourse = firstUncompletedItemInCourse || orderedItems[0];

      const itemTypename = firstItemInCourse?.contentSummary?.__typename;

      if (!itemTypename || !firstItemInCourse) {
        return courseHomeLink;
      }

      const itemTypeSlug = itemTypename.replace('OnDemandLearnerMaterialItemsV1_', '').replace('Member', '');

      return `/learn/${courseSlug}/${itemTypeSlug}/${firstItemInCourse.itemId}`;
    } catch {
      return courseHomeLink;
    }
  }, [courseId, courseSlug, getCourseItemInfo, getItemsInCourse]);

  return { fetchSkillPathwayCourseStartLink };
};

export default useGetSkillPathwayCourseStartLink;
